import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";
import { DirectoryEmailSchema } from "./workspace-user-schemas";

/**
 * Setup guidance and field defaults only (ADR-0059). This must never change protocol behaviour:
 * one provider-neutral flow validates every connection identically, whatever this says.
 */
export const SamlProviderHintSchema = z.enum(["entra", "okta", "google", "generic"]);

/**
 * `draft` is configured but unusable. `testing` accepts only an administrator's test, which issues
 * no session. `enabled` is normal login. `disabled` refuses everything — note it does not revoke
 * sessions already issued; cutting access is membership suspension.
 */
export const SamlConnectionStatusSchema = z.enum(["draft", "testing", "enabled", "disabled"]);

/**
 * Providers disagree here, which is the single most common setup failure: Google Workspace's
 * NameID is the primary email, Entra ID defaults to a persistent pairwise identifier, and Okta is
 * configurable. The NameID is always the durable subject; the email comes from `emailAttribute`
 * unless the format says the NameID already is one.
 */
export const SamlNameIdFormatSchema = z.enum(["emailAddress", "persistent", "unspecified"]);

/**
 * A SAML attribute name, as the provider emits it — Entra uses claim URIs, Okta and Google use
 * short names, so this cannot be an enum.
 */
export const SamlAttributeNameSchema = z.string().trim().min(1).max(256);

export const SamlEntityIdSchema = z.string().trim().min(1).max(1024);

/**
 * Whether a string is an https URL carrying no credentials.
 *
 * Written to be total. Zod runs a `.refine()` even after `z.url()` has already rejected the value,
 * and `new URL` throws on something unparseable rather than returning null — so a refine that
 * assumed it would parse threw out of `safeParse` instead of failing validation. That surfaced as a
 * 500 from the connection endpoint and an unhandled error in the setup form, for the ordinary case
 * of an administrator saving before filling the field in.
 */
function isHttpsUrlWithoutCredentials(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return url.protocol === "https:" && url.username === "" && url.password === "";
}

/** An identity provider's sign-on endpoint. Always https, never ported, never carrying credentials. */
export const SamlSsoUrlSchema = z
  .url()
  .max(2048)
  .refine(isHttpsUrlWithoutCredentials, "Enter the provider's HTTPS sign-on URL.");

/**
 * Every reason a login can be refused, as a closed set.
 *
 * These are written to audit rows and shown to an administrator in a connection test. They are
 * deliberately NOT shown to the person signing in — an end user sees one of the coarse failures
 * below instead, so a misconfigured connection cannot be used to probe which addresses exist.
 */
export const SamlValidationCheckSchema = z.enum([
  "connection_unavailable",
  "malformed",
  "doctype_forbidden",
  "too_large",
  "status_not_success",
  "assertion_count",
  "signature_missing",
  "signature_invalid",
  "signature_algorithm",
  "certificate_unknown",
  "certificate_expired",
  "encrypted_unsupported",
  "issuer_mismatch",
  "audience_mismatch",
  "destination_mismatch",
  "recipient_mismatch",
  "conditions_expired",
  "conditions_not_yet_valid",
  "subject_confirmation",
  "in_response_to_unknown",
  "unsolicited_not_allowed",
  "replayed",
  "name_id_missing",
  "email_missing",
  "not_provisioned",
  "membership_missing",
  /** The workspace's sign-in policy no longer offers single sign-on (ADR-0061). */
  "method_disabled",
  /** The sign-in began on the neutral shell, and the workspace requires its own address (ADR-0063). */
  "workspace_host_required",
  "rate_limited",
]);

/**
 * What the person signing in is told. Coarse on purpose: the diagnostic detail lives in the audit
 * row and the administrator's test report, which is what "fails safely with a useful error" means
 * here — useful to the administrator, not to an attacker.
 */
export const SamlLoginFailureSchema = z.enum([
  "saml_unavailable",
  /** The workspace turned single sign-on off in its own policy (ADR-0061). */
  "saml_disabled",
  /** The workspace only accepts a sign-in that begins at its own address (ADR-0063). */
  "saml_workspace_host_required",
  "saml_failed",
  "saml_not_provisioned",
  "saml_rate_limited",
  "saml_replayed",
]);

export const SamlCertificateViewSchema = z.strictObject({
  id: OpaqueIdSchema,
  fingerprintSha256: z.string().length(64),
  notBefore: z.number().int(),
  notAfter: z.number().int(),
  status: z.enum(["active", "retired"]),
  createdAt: z.number().int(),
});

/**
 * The identifiers an administrator gives their identity provider. Derived from the shell origin and
 * the connection id, never stored, so metadata and the ACS can never disagree.
 */
export const SamlServiceProviderIdentifiersSchema = z.strictObject({
  entityId: z.url(),
  acsUrl: z.url(),
  metadataUrl: z.url(),
});

export const SamlConnectionViewSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  displayName: z.string().min(2).max(120),
  providerHint: SamlProviderHintSchema,
  status: SamlConnectionStatusSchema,
  idpEntityId: SamlEntityIdSchema,
  idpSsoUrl: z.url(),
  nameIdFormat: SamlNameIdFormatSchema,
  emailAttribute: SamlAttributeNameSchema.nullable(),
  allowIdpInitiated: z.boolean(),
  lastTestSucceededAt: z.number().int().nullable(),
  enabledAt: z.number().int().nullable(),
  identifiers: SamlServiceProviderIdentifiersSchema,
  certificates: z.array(SamlCertificateViewSchema).max(16),
});

export const SamlConnectionCreateSchema = z.strictObject({
  displayName: z.string().trim().min(2).max(120),
  providerHint: SamlProviderHintSchema,
  idpEntityId: SamlEntityIdSchema,
  idpSsoUrl: SamlSsoUrlSchema,
  nameIdFormat: SamlNameIdFormatSchema,
  emailAttribute: SamlAttributeNameSchema.nullable().default(null),
  allowIdpInitiated: z.boolean().default(false),
});

export const SamlConnectionUpdateSchema = SamlConnectionCreateSchema;

/**
 * Only these transitions are offered. `enabled` additionally requires a passing test, which the
 * database enforces, so this schema is a convenience rather than the control.
 */
export const SamlConnectionStatusUpdateSchema = z.strictObject({
  status: z.enum(["draft", "testing", "enabled", "disabled"]),
});

/** A PEM or bare-base64 X.509 certificate, as pasted out of a provider's console. */
export const SamlCertificateSubmitSchema = z.strictObject({
  certificate: z.string().trim().min(1).max(16384),
});

/**
 * The result of a connection test: every check that ran, and whether a user would have resolved.
 *
 * `nameIdPreview` and `emailPreview` are deliberately partial — an administrator needs enough to
 * recognise a mapping mistake, not a readable copy of someone's identity.
 */
export const SamlTestReportSchema = z.strictObject({
  outcome: z.enum(["succeeded", "failed"]),
  failedCheck: SamlValidationCheckSchema.nullable(),
  checksPassed: z.array(SamlValidationCheckSchema).max(32),
  nameIdPreview: z.string().max(64).nullable(),
  emailPreview: z.string().max(64).nullable(),
  wouldResolveUser: z.boolean(),
  completedAt: z.number().int(),
});

export const SamlConnectionResponseSchema = z.strictObject({
  connection: SamlConnectionViewSchema.nullable(),
  testReport: SamlTestReportSchema.nullable(),
});

/** What a branded workspace login page needs to decide whether to offer an SSO button. */
export const WorkspaceSsoAvailabilitySchema = z.strictObject({
  available: z.boolean(),
  displayName: z.string().min(2).max(120).nullable(),
  startPath: z.string().startsWith("/").max(2048).nullable(),
});

export const SamlDirectoryRegistrationSchema = z.strictObject({
  email: DirectoryEmailSchema,
});

export type SamlProviderHint = z.infer<typeof SamlProviderHintSchema>;
export type SamlConnectionStatus = z.infer<typeof SamlConnectionStatusSchema>;
export type SamlNameIdFormat = z.infer<typeof SamlNameIdFormatSchema>;
export type SamlValidationCheck = z.infer<typeof SamlValidationCheckSchema>;
export type SamlLoginFailure = z.infer<typeof SamlLoginFailureSchema>;
export type SamlCertificateView = z.infer<typeof SamlCertificateViewSchema>;
export type SamlServiceProviderIdentifiers = z.infer<typeof SamlServiceProviderIdentifiersSchema>;
export type SamlConnectionView = z.infer<typeof SamlConnectionViewSchema>;
export type SamlConnectionCreate = z.infer<typeof SamlConnectionCreateSchema>;
export type SamlConnectionStatusUpdate = z.infer<typeof SamlConnectionStatusUpdateSchema>;
export type SamlCertificateSubmit = z.infer<typeof SamlCertificateSubmitSchema>;
export type SamlTestReport = z.infer<typeof SamlTestReportSchema>;
export type SamlConnectionResponse = z.infer<typeof SamlConnectionResponseSchema>;
export type WorkspaceSsoAvailability = z.infer<typeof WorkspaceSsoAvailabilitySchema>;
